import {
  Field,
  FieldError,
  FieldLabel,
  type ItemOptionProps,
  MultiSelectSearch,
} from "@/components/ui";
import type {
  ControllerFieldState,
  ControllerRenderProps,
} from "react-hook-form";
import {
  type AOEQuestion,
  AOEQuestionAnswerType,
  isAOEAnswerCoding,
} from "../../../network/useRetrieveAOEForOrder";
import type { AOEFormSchema } from "../schemas";

export const MultiSelectQuestion = ({
  question,
  field,
  fieldState,
  disabled,
}: {
  question: AOEQuestion;
  field: ControllerRenderProps<
    AOEFormSchema,
    `questionnaires.${string}.${string}`
  >;
  fieldState: ControllerFieldState;
  disabled: boolean;
}) => {
  const values = Array.isArray(field.value)
    ? field.value
    : field.value
      ? [field.value]
      : [];

  const selectedOptions = values?.map((value) => {
    if (isAOEAnswerCoding(value)) {
      return {
        label: value.display,
        value: value.code,
      };
    }
    return {
      label: value,
      value: value,
    };
  });

  const options =
    question.answerOptions?.map((option) => ({
      value: option.valueCoding?.code ?? option.valueString ?? "",
      label: option.valueCoding?.display ?? option.valueString ?? "",
      answerType: option.valueCoding
        ? AOEQuestionAnswerType.CODING
        : AOEQuestionAnswerType.TEXT,
    })) ?? [];

  const handleItemAdded = (item: ItemOptionProps) => {
    const option = options.find((option) => option.value === item.value);
    if (option?.answerType === AOEQuestionAnswerType.CODING) {
      const newValues = [
        ...(values ?? []),
        {
          code: item.value,
          display: item.label,
        },
      ];
      field.onChange(newValues);
    } else {
      const newValues = [...(values ?? []), item.value];
      field.onChange(newValues);
    }
  };

  const handleItemRemoved = (item: ItemOptionProps) => {
    const newValues = [...(values ?? [])].filter((value) => {
      const isCoding = isAOEAnswerCoding(value);
      const isEqual = isCoding
        ? value.code === item.value
        : value === item.value;
      return !isEqual;
    });
    field.onChange(newValues);
  };

  return (
    <Field data-invalid={fieldState.invalid}>
      <FieldLabel htmlFor={field.name}>
        {question.required && "*"}
        {question.question}
      </FieldLabel>
      <MultiSelectSearch
        options={options}
        selectedItems={selectedOptions}
        placeholder=""
        onItemAdded={handleItemAdded}
        onItemRemoved={handleItemRemoved}
        disabled={disabled}
      />
      <FieldError errors={[fieldState.error]} />
    </Field>
  );
};
